import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from 'react-router-dom';
import { useAuth } from "../AuthContext";
import { useTranslation } from 'react-i18next';
import SiteFooter from './common/SiteFooter';
import Register from './Register';
import './Login.css'; // Import the CSS styles

export default function Login() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [showInactivity, setShowInactivity] = useState(false);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { login, signInWithOAuth, user, logoutReason, clearLogoutReason } = useAuth();
  const navigate = useNavigate();
  const formRef = useRef(null);
  // const location = useLocation();
  // const params = new URLSearchParams(location.search);
  // const sessionExpired = params.get("expired") === "true";

  const [, setCurrentSlide] = useState(0);
  const [showRegister, setShowRegister] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!showRegister) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [showRegister]);
  useEffect(() => {
    if (!showLogin) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [showLogin]);
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % 5);
    }, 6000);
    return () => clearInterval(interval);
    // run only once on mount
  }, []);

  useEffect(() => {
    const rememberedEmail = localStorage.getItem("rememberEmail");
    if (rememberedEmail) {
        setEmail(rememberedEmail);
        setRememberMe(true);
    }
  }, []);

  useEffect(() => {
    async function checkUser() {
      if (user !== null) {
        navigate('/dashboard');
      }
    }
    checkUser();
  }, [user, navigate]);

  // Show a floating toast when session expired due to inactivity
  useEffect(() => {
    if (logoutReason === 'inactivity') {
      setShowInactivity(true);
      const timer = setTimeout(() => setShowInactivity(false), 6000);
      return () => clearTimeout(timer);
    }
    if (logoutReason) {
        setError(t(`login.${logoutReason}`, 'Your session has expired. Please log in again.'));
        clearLogoutReason?.();
    }
  }, [logoutReason, clearLogoutReason, t]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!email || !password) {
        setError(t('login.errorFill', 'Please complete all fields.'));
        return;
    }

    try {
        setIsLoading(true);
        setError(null);
        await login(email, password);

        if (rememberMe) {
            localStorage.setItem('rememberEmail', email);
        } else {
            localStorage.removeItem('rememberEmail');
        }

        navigate('/dashboard');
    } catch (error) {
        console.error('Login error:', error);
        const errorMessage = error.error_description || error.message || t('login.errorGeneric', 'Login failed. Please try again.');
        setError(errorMessage);
    } finally {
        setIsLoading(false);
    }
  };

  // La cuenta demo tiene el plan 'demo' (sólo lectura): su clave va en el bundle a propósito.
  const demoEmail = process.env.REACT_APP_DEMO_EMAIL;
  const demoPassword = process.env.REACT_APP_DEMO_PASSWORD;
  const demoEnabled = Boolean(demoEmail && demoPassword);

  const handleGoogleLogin = async () => {
    try {
        setIsLoading(true);
        setError(null);
        // Redirige a Google; al volver, AuthContext detecta la sesión y navega al dashboard.
        await signInWithOAuth('google');
    } catch (error) {
        console.error('Google login error:', error);
        setError(t('login.errorGoogle', 'No se pudo ingresar con Google. Intenta de nuevo.'));
        setIsLoading(false);
    }
  };

  const handleDemoLogin = async () => {

    try {
        setIsLoading(true);
        setError(null);
        await login(demoEmail, demoPassword);
        navigate('/dashboard');
    } catch (error) {
        setError(t('login.errorDemo', 'Failed to log in with demo account. Please try again.'));
    } finally {
        setIsLoading(false);
    }
  };

  return (
    <>
    <div className="login-page">
      {showInactivity && (
        <div
          role="status"
          aria-live="polite"
          aria-atomic="true"
          style={{
            position: 'fixed',
            top: 12,
            right: 12,
            zIndex: 2000,
            maxWidth: 420,
            background: '#ffffff',
            border: '1px solid #e2e8f0',
            boxShadow: '0 12px 30px rgba(2,6,23,0.12)',
            borderRadius: 12,
            padding: '0.75rem 0.875rem 0.75rem 0.75rem',
            display: 'grid',
            gridTemplateColumns: '1fr auto',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          <div style={{ lineHeight: 1.3 }}>
            <div style={{ fontWeight: 600, color: '#0f172a' }}>
              {t('login.sessionExpiredTitle', 'Sesión expirada')}
            </div>
            <div className="text-sm" style={{ color: '#334155' }}>
              {t('login.inactivityMessage', 'Tu sesión ha expirado por inactividad. Inicia sesión nuevamente.')}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setShowInactivity(false)}
            aria-label={t('common.close', 'Cerrar')}
            title={t('common.close', 'Cerrar')}
            style={{
              border: 'none',
              background: 'transparent',
              fontSize: 18,
              lineHeight: 1,
              color: '#64748b',
              cursor: 'pointer',
              padding: 4
            }}
          >
            ×
          </button>
        </div>
      )}
      <div className={`login-header ${isScrolled ? 'is-scrolled' : 'is-top'}`}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          zIndex: 1000,
          paddingBottom: 0
        }}
      >
        <img
          src="/assets/zenda-logo.svg"
          alt="Zenda logo"
          className="logo-fixed"
          style={{
            display: 'block',
            margin: '0 auto',
            position: 'relative',
            top: '1rem',
            height: '70px',
            zIndex: 1000,
          }}
        />
        <div
          className="top-login-bar login-align-right"
          style={{
            margin: '1rem auto 0 auto', // center horizontally
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center', // center the elements
            alignItems: 'center',
            gap: '0.5rem',
            maxWidth: '1100px' // optional: constrain width
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            <button
              type="button"
              className="gray-button"
              onClick={() => setShowLogin(true)}
              aria-label={t('login.openSignIn', 'Ingresar')}
              disabled={isLoading}
            >
              {t('login.openSignIn', 'Ingresar')}
            </button>

            {demoEnabled && (
              <button
                type="button"
                onClick={handleDemoLogin}
                className="gray-button"
                aria-label={t('login.tryDemo', 'Ver demo')}
                disabled={isLoading}
              >
                {t('login.tryDemo', 'Ver demo')}
              </button>
            )}

            <button
              onClick={(e) => { e.preventDefault(); setShowRegister(true); }}
              className="signup-special-button"
              style={{ textDecoration: 'none', whiteSpace: 'nowrap' }}
            >
              {t('login.signUp', 'Crear cuenta')}
            </button>
          </div>
        </div>
      </div>

      {/* ---------- Hero + Texto + Feature Grid ---------- */}
      <section
        className="hero-grid"
        style={{
          maxWidth: '1200px',
          margin: '4rem auto 0',
          padding: '0 1rem',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gridTemplateRows: 'auto auto',
          gap: '2.5rem',
          alignItems: 'stretch',
          marginTop: 'clamp(9rem, 11vw, 12rem)',
        }}
      >
        {/* Hero image top-left */}
        <figure
          style={{
            margin: 0,
            borderRadius: 16,
            overflow: 'hidden',
            border: '1px solid #e5e7eb',
            height: '360px',
            boxShadow: '0 6px 20px rgba(0,0,0,0.06)',
          }}
        >
          <img
            src="/assets/hero.jpg"
            alt={t('login.heroAlt', 'Vista general del dashboard de CartolaPro')}
            style={{ width: '100%', height: '100%', display: 'block', objectFit: 'cover' }}
            loading="lazy"
          />
        </figure>

        {/* Texto a la derecha del hero */}
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <h1 className="lp-h1" style={{ margin: 0 }}>
            {t('login.heroTitle', 'Tus finanzas, claras y simples')}
          </h1>
          <p
            className="lp-lead"
            style={{
              opacity: 0.85,
              marginTop: '1.25rem',
              lineHeight: 1.5,
            }}
          >
            {t(
              'login.heroSubtitle',
              'CartolaPro transforma tus cartolas bancarias y de tarjetas en un panel claro y visual. Detecta tus gastos por categoría, cuotas o suscripciones, y entiende cómo evoluciona tu presupuesto mes a mes sin esfuerzo. Ideal para hogares que buscan ordenar sus finanzas y tomar mejores decisiones con datos reales.'
            )}
          </p>
        </div>

        {/* Feature 1 bottom-left */}
        <figure
          style={{
            margin: 0,
            borderRadius: 16,
            overflow: 'hidden',
            border: '1px solid #e5e7eb',
            height: '360px',
            boxShadow: '0 6px 20px rgba(0,0,0,0.06)',
            position: 'relative',
          }}
        >
          <img
            src="/assets/feature1.jpg"
            alt={t('login.feature1Alt', 'Clasificación automática por categorías')}
            style={{ width: '100%', height: '100%', display: 'block', objectFit: 'cover' }}
            loading="lazy"
          />
          <figcaption
            style={{
              position: 'absolute',
              left: 12,
              bottom: 12,
              background: 'rgba(255,255,255,0.9)',
              padding: '6px 10px',
              borderRadius: 8,
              fontWeight: 600,
              backdropFilter: 'blur(4px)',
              border: '1px solid rgba(0,0,0,0.06)',
            }}
          >
            {t('login.feature1', 'Categorización automática')}
          </figcaption>
        </figure>

        {/* Feature 2 bottom-right */}
        <figure
          style={{
            margin: 0,
            borderRadius: 16,
            overflow: 'hidden',
            border: '1px solid #e5e7eb',
            height: '360px',
            boxShadow: '0 6px 20px rgba(0,0,0,0.06)',
            position: 'relative',
          }}
        >
          <img
            src="/assets/feature2.jpg"
            alt={t('login.feature2Alt', 'Seguimiento de cuotas y suscripciones')}
            style={{ width: '100%', height: '100%', display: 'block', objectFit: 'cover' }}
            loading="lazy"
          />
          <figcaption
            style={{
              position: 'absolute',
              left: 12,
              bottom: 12,
              background: 'rgba(255,255,255,0.9)',
              padding: '6px 10px',
              borderRadius: 8,
              fontWeight: 600,
              backdropFilter: 'blur(4px)',
              border: '1px solid rgba(0,0,0,0.06)',
            }}
          >
            {t('login.feature2', 'Cuotas y suscripciones')}
          </figcaption>
        </figure>
      </section>

      {/* ---------- Funcionalidades & Ventajas ---------- */}
      <section
        className="lp-features-benefits"
        style={{ maxWidth: '1200px', margin: '5rem auto ', padding: '0 1rem' }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1.1fr 0.9fr',
            gap: '1rem',
          }}
        >
          {/* Funcionalidades */}
          <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: '1rem' }}>
            <h2 style={{ margin: 0, marginBottom: '0.75rem' }}>
              {t('login.featuresBenefits.featuresTitle', 'Funcionalidades')}
            </h2>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: '0.75rem',
                marginTop: '0.25rem'
              }}
            >
              {[ 
                { icon: '📥', title: t('login.fb.import', 'Importación de cartolas'), text: t('login.fb.importText', 'PDF/CSV de bancos') },
                { icon: '🏷️', title: t('login.fb.autoCat', 'Categorización automática'), text: t('login.fb.autoCatText', 'Aprende de tus correcciones') },
                { icon: '🔁', title: t('login.fb.recurring', 'Suscripciones y cuotas'), text: t('login.fb.recurringText', '1/10, vencimientos y alertas') },
                { icon: '📊', title: t('login.fb.reports', 'Dashboards y reportes'), text: t('login.fb.reportsText', 'Resumen, comparativos y tendencias') },
                { icon: '🎯', title: t('login.fb.budgets', 'Presupuestos y metas'), text: t('login.fb.budgetsText', 'Define, mide y ajusta') },
                { icon: '🧩', title: t('login.fb.tags', 'Etiquetas personalizadas'), text: t('login.fb.tagsText', 'Filtra y combina tu vista') },
              ].map(({ icon, title, text }, i) => (
                <div key={i} style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', border: '1px solid #f1f5f9', borderRadius: 10, padding: '0.6rem 0.75rem' }}>
                  <div style={{ fontSize: 22 }} aria-hidden>{icon}</div>
                  <div>
                    <div style={{ fontWeight: 600 }}>{title}</div>
                    <div className="text-sm" style={{ opacity: 0.8 }}>{text}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Ventajas */}
          <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, padding: '1rem' }}>
            <h2 style={{ margin: 0, marginBottom: '0.75rem' }}>
              {t('login.featuresBenefits.benefitsTitle', 'Ventajas')}
            </h2>
            <ul style={{ listStyle: 'none', padding: 0, marginTop: '2rem', display: 'grid', gap: '0.5rem' }}>
              {[
                t('login.fb.benefit1', 'Ahorra horas de Excel cada mes'),
                t('login.fb.benefit2', 'Mira el gasto real del hogar en CLP'),
                t('login.fb.benefit3', 'Detecta cargos recurrentes y evita sorpresas'),
                t('login.fb.benefit4', 'Decisiones rápidas con dashboards claros'),
              ].map((item, idx) => (
                <li key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span aria-hidden style={{ fontSize: 18 }}>✅</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <div style={{ marginTop: '0.85rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              <span className="text-xs" style={{ background: '#f1f5f9', border: '1px solid #e5e7eb', padding: '4px 8px', borderRadius: 999 }}>{t('login.fb.badgeLocal', 'Hecho para Chile')}</span>
              <span className="text-xs" style={{ background: '#f1f5f9', border: '1px solid #e5e7eb', padding: '4px 8px', borderRadius: 999 }}>{t('login.fb.badgePrivacy', 'Tus datos son tuyos')}</span>
            </div>
          </div>
        </div>
      </section>

      {/* ---------- Cómo funciona ---------- */}
      <section
        className="how-it-works"
        style={{ maxWidth: '1200px', margin: '5rem auto 0', padding: '0 1rem' }}
      >
        <h2 style={{ margin: 0, marginBottom: '0.75rem' }}>
          {t('login.hiw.title', '¿Cómo funciona?')}
        </h2>
        <p style={{ marginTop: 6, opacity: 0.85 }}>
          {t(
            'login.hiw.subtitle',
            'Sigue estos pasos y en pocos minutos tendrás claridad total sobre tus finanzas.'
          )}
        </p>

        {/* Ilustración explicativa */}
        <figure
          style={{
            margin: '1rem 0 0.75rem 0',
            borderRadius: 16,
            overflow: 'hidden',
            border: '1px solid #e5e7eb',
            boxShadow: '0 6px 20px rgba(0,0,0,0.06)',
            maxWidth: '100%',
            height: 'clamp(200px, 22vw, 320px)'
          }}
        >
          <img
            src="/assets/how-it-works.png"
            alt={t('login.hiw.illustrationAlt', 'Flujo de uso: subir cartolas → categorización → dashboard → metas → mantener finanzas al día')}
            style={{ width: '100%', height: '100%', display: 'block', objectFit: 'contain', objectPosition: 'center center' }}
            loading="lazy"
          />
        </figure>

        <ol
          style={{
            listStyle: 'none',
            padding: 0,
            margin: '1rem 0 0 0',
            display: 'flex',
            flexWrap: 'nowrap',
            alignItems: 'stretch',
            justifyContent: 'space-between',
            gap: '0.75rem'
          }}
        >
          {/* Paso 1 */}
          <li
            style={{
              flex: '1 1 0',
              minWidth: 0,
              border: '1px solid #e5e7eb',
              borderRadius: 12,
              padding: '0.9rem',
              display: 'flex',
              gap: '0.75rem',
              alignItems: 'flex-start',
              background: '#ffffff'
            }}
          >
            <div aria-hidden style={{ fontSize: 24, lineHeight: 1, marginTop: 2 }}>📥</div>
            <div>
              <div className="text-sm" style={{ opacity: 0.8 }}>
                {t('login.hiw.step1.text', 'Carga archivos PDF o CSV de tus bancos y tarjetas.')}
              </div>
            </div>
          </li>

          {/* Paso 2 */}
          <li
            style={{
              flex: '1 1 0',
              minWidth: 0,
              border: '1px solid #e5e7eb',
              borderRadius: 12,
              padding: '0.9rem',
              display: 'flex',
              gap: '0.75rem',
              alignItems: 'flex-start',
              background: '#ffffff'
            }}
          >
            <div aria-hidden style={{ fontSize: 24, lineHeight: 1, marginTop: 2 }}>🤖</div>
            <div>
              <div className="text-sm" style={{ opacity: 0.8 }}>
                {t('login.hiw.step2.text', 'La IA interpreta los movimientos y los organiza por categoría, cuotas y suscripciones.')}
              </div>
            </div>
          </li>

          {/* Paso 3 */}
          <li
            style={{
              flex: '1 1 0',
              minWidth: 0,
              border: '1px solid #e5e7eb',
              borderRadius: 12,
              padding: '0.9rem',
              display: 'flex',
              gap: '0.75rem',
              alignItems: 'flex-start',
              background: '#ffffff'
            }}
          >
            <div aria-hidden style={{ fontSize: 24, lineHeight: 1, marginTop: 2 }}>📊</div>
            <div>
              <div className="text-sm" style={{ opacity: 0.8 }}>
                {t('login.hiw.step3.text', 'Ve el gasto por categoría, compara meses y detecta tendencias.')}
              </div>
            </div>
          </li>

          {/* Paso 4 */}
          <li
            style={{
              flex: '1 1 0',
              minWidth: 0,
              border: '1px solid #e5e7eb',
              borderRadius: 12,
              padding: '0.9rem',
              display: 'flex',
              gap: '0.75rem',
              alignItems: 'flex-start',
              background: '#ffffff'
            }}
          >
            <div aria-hidden style={{ fontSize: 24, lineHeight: 1, marginTop: 2 }}>🎯</div>
            <div>
              <div className="text-sm" style={{ opacity: 0.8 }}>
                {t('login.hiw.step4.text', 'Corrige categorías si lo necesitas, agrega etiquetas y fija presupuestos por rubro.')}
              </div>
            </div>
          </li>

          {/* Paso 5 */}
          <li
            style={{
              flex: '1 1 0',
              minWidth: 0,
              border: '1px solid #e5e7eb',
              borderRadius: 12,
              padding: '0.9rem',
              display: 'flex',
              gap: '0.75rem',
              alignItems: 'flex-start',
              background: '#ffffff'
            }}
          >
            <div aria-hidden style={{ fontSize: 24, lineHeight: 1, marginTop: 2 }}>🗓️</div>
            <div>
              <div className="text-sm" style={{ opacity: 0.8 }}>
                {t('login.hiw.step5.text', 'Revisa avances, ajusta metas y mantén el control mes a mes.')}
              </div>
            </div>
          </li>

        </ol>
      </section>

      {/* ---------- CTA mínima ---------- */}
      <section style={{ maxWidth: '1200px', margin: '5rem auto 5rem', padding: '0 1rem', textAlign: 'center' }}>
        <p style={{ margin: '0.5rem 0 1rem', opacity: 0.9 }}>{t('login.ctaMini', 'Regístrate gratis y sube tu primera cartola ahora')}</p>
        <button
          onClick={(e) => {
            e.preventDefault();
            setShowRegister(true);
          }}
          className="signup-special-button"
          style={{ textDecoration: 'none' }}
        >
          {t('login.ctaButton', 'Crear mi cuenta gratis')}
        </button>
      </section>

      <SiteFooter />
      {showLogin && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={t('login.signIn', 'Iniciar sesión')}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(2,6,23,0.45)',
            display: 'grid',
            placeItems: 'center',
            zIndex: 3000
          }}
        >
          <div
            className="auth-modal"
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 'min(400px, 92vw)',
              borderRadius: 12,
              position: 'relative',
              height: 'auto',
              minHeight: '340px',
              transform: 'translateY(-160px)',
            }}
          >
            <button
              type="button"
              onClick={() => setShowLogin(false)}
              aria-label={t('common.close', 'Cerrar')}
              style={{
                position: 'absolute',
                top: 8,
                right: 8,
                border: 'none',
                background: 'transparent',
                fontSize: 22,
                lineHeight: 1,
                cursor: 'pointer',
                color: '#64748b'
              }}
            >
              ×
            </button>
            <div style={{ textAlign: 'center', marginBottom: '0.75rem' }}>
              <img
                src="/assets/cartolapro.svg"
                alt="CartolaPro logo"
                className="logo-fixed"
                style={{ display: 'block', margin: '0 auto', height: '42px' }}
              />
            </div>
            <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
              <h3 className="text-xl" style={{ margin: 0, fontWeight: 700 }}>
                {t('login.signIn', 'Ingresar')}
              </h3>
            </div>
            { error && <div className="error-message">{error}</div> }

            <form ref={formRef} className="auth-form" onSubmit={async (e) => { await handleSubmit(e); setShowLogin(false); }}>
              <div style={{ display: 'grid', gap: '0.6rem' }}>
                <label htmlFor="email-modal" style={{ fontWeight: 600 }}>
                  {t('login.emailAddress', 'Correo electrónico')}
                </label>
                <input
                  type="email"
                  id="email-modal"
                  name="email"
                  autoComplete="username email"
                  inputMode="email"
                  autoCapitalize="none"
                  placeholder={t('login.emailPlaceholder', 'andres@correo.cl')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="text-base"
                  style={{ padding: '5px 8px', height: '28px', borderRadius: 10, border: '1px solid #cbd5e1' }}
                  disabled={isLoading}
                />

                <label htmlFor="password-modal" style={{ fontWeight: 600 }}>
                  {t('login.password', 'Contraseña')}
                </label>
                <input
                  type="password"
                  id="password-modal"
                  name="password"
                  autoComplete="current-password"
                  placeholder={t('login.passwordPlaceholder', 'Password')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="text-base"
                  style={{ padding: '5px 8px', height: '28px', borderRadius: 10, border: '1px solid #cbd5e1' }}
                  disabled={isLoading}
                />

                <label htmlFor="rememberMe-modal" className="text-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.25rem', userSelect: 'none', lineHeight: 1.2 }}>
                  <input
                    type="checkbox"
                    id="rememberMe-modal"
                    name="rememberMe"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    aria-checked={rememberMe}
                    style={{ transform: 'scale(0.95)', transformOrigin: 'left center' }}
                    disabled={isLoading}
                  />
                  {t('login.rememberMe', 'Recordarme')}
                </label>

                <div
                  className="actions"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '0.5rem',
                    marginTop: '0.5rem'
                  }}
                >
                  {/* Left: prompt + Create Account link (same format as Register) */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span className="text-sm" style={{ fontSize: '12px', color: '#475569', lineHeight: 1 }}>
                      {t('login.noAccount', '¿No tienes cuenta?')}
                    </span>
                    <button
                      onClick={(e) => { e.preventDefault(); setShowLogin(false); setShowRegister(true); }}
                      className="text-sm link-underline-hover"
                      style={{ fontSize: '12px', color: '#2563eb', fontWeight: 600, lineHeight: 1 }}
                    >
                      {t('login.createAccount', 'Crear cuenta')}
                    </button>
                  </div>

                  {/* Right: action buttons */}
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button type="button" className="gray-button" onClick={() => setShowLogin(false)}>
                      {t('common.cancel', 'Cancelar')}
                    </button>
                    <button type="submit" className="signup-special-button" disabled={isLoading}>
                      {t('login.signIn', 'Ingresar')}
                    </button>
                  </div>
                </div>
              </div>
            </form>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', margin: '0.9rem 0 0.6rem', color: '#94a3b8', fontSize: '12px' }}>
              <span style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
              {t('login.or', 'o')}
              <span style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
            </div>
            <button
              type="button"
              className="gray-button"
              onClick={handleGoogleLogin}
              disabled={isLoading}
              style={{ width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}
            >
              <svg width="16" height="16" viewBox="0 0 48 48" aria-hidden="true">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              </svg>
              {t('login.google', 'Continuar con Google')}
            </button>
          </div>
        </div>
      )}
      {showRegister && <Register asModal onClose={() => setShowRegister(false)} />}
      </div>
    </>
  );
}